import {
    Contact,
    NotificationPayload,
    PaginationParams,
    PaginationResponse,
    PatientAgeRange,
    ResponseType,
    TableFiltersPayload,
    TableOrder
} from '@/types.ts'
import axios, {parseResponseData} from '@/axiosInstance.ts'
import {z} from 'zod'
import {
    PatientDisability,
    PatientExtended,
    PatientOccupation,
    PatientQualification,
    PatientsColumn,
    PatientSmall,
    PatientStatus,
    PatientTaxCodeType,
    PatientTransportationMode,
    PatientLastInfo,
    PatientEventsColumn
} from '@/features/patients/types'
import {captureException} from '@sentry/react'
import {EventSmall} from '@/features/agenda/types'

/*Get patients*/
export interface HttpGetPatientsOptions {
    params: PaginationParams & {
        search?: string
        responseType?: ResponseType
    }
    payload?: TableFiltersPayload & {
        sorts?: TableOrder[]
    }
}
export const httpGetPatients = ({params, payload}: HttpGetPatientsOptions) =>
    axios.post('patients/index', payload, {params})

/*Delete patient*/
export const httpDeletePatient = ({urlParams}: {urlParams: {patientId: string}}) =>
    axios.delete(`patients/${urlParams.patientId}`)

/*Export patient*/
interface HttpExportPatientOptions {
    payload: {
        selectedPatientIds: string[]
        exceptedPatientIds: string[]
        type: 'xlsx' | 'xls' | 'csv'
        filters?: TableFiltersPayload['filters']
        fields?: PatientsColumn[]
        sorts?: TableOrder[]
    }
}
export const httpExportPatient = ({payload}: HttpExportPatientOptions) =>
    axios.post('patients/export', payload, {responseType: 'blob'})

/*Get neuromuscular diagnosis*/
export type HttpGetNeuromuscularDiagnosisOptions = {
    search?: string
    responseType: ResponseType
} & PaginationParams

export const httpGetNeuromuscularDiagnosis = (params: HttpGetNeuromuscularDiagnosisOptions) =>
    axios.get(`neuromuscular-diagnoses`, {params})

/*Create patient*/
export const HttpCreatePatientPayload = z
    .object({
        lastName: z.string(),
        firstName: z.string(),
        gender: z.string(),
        birthCityId: z.string().optional(),
        birthCountryId: z.string(),
        maritalStatus: z.string(),
        birthDate: z.string(),
        citizenshipId: z.string(),
        taxCodeType: PatientTaxCodeType,
        taxCode: z.string(),
        residenceAslDistrictId: z.string().optional(),
        residence: z
            .object({
                address: z.string(),
                streetNumber: z.string(),
                city: z.string(),
                postalCode: z.string(),
                latitude: z.string().optional(),
                longitude: z.string().optional(),
                municipalityId: z.string().optional()
            })
            .optional(),
        domicileAslDistrictId: z.string().optional(),
        domicile: z
            .object({
                address: z.string(),
                streetNumber: z.string(),
                city: z.string(),
                postalCode: z.string(),
                latitude: z.string().optional(),
                longitude: z.string().optional(),
                municipalityId: z.string().optional()
            })
            .optional(),
        contacts: z.array(Contact).min(2),
        education: PatientQualification,
        occupation: PatientOccupation,
        serviceType: PatientAgeRange,
        protectedCategory: z.boolean().default(false),
        legalProtection: z.boolean().default(false),
        transportationMode: PatientTransportationMode,
        localAuthorityAssistanceService: z.boolean().default(false),
        disability: PatientDisability,
        fm01: z.boolean().default(false),
        privacy1: z.boolean().default(false),
        privacy2: z.boolean().default(false),
        privacy3: z.boolean().default(false),
        privacy4: z.boolean().default(false),
        consentDateFm01: z.string().optional(),
        notes: z.string().optional(),
        neuromuscularDiagnosisId: z.string().optional()
    })
    .extend(NotificationPayload.shape)
export type HttpCreatePatientPayload = z.infer<typeof HttpCreatePatientPayload>

type HttpCreatePatientOptions = {
    params: {responseType: ResponseType}
    payload: HttpCreatePatientPayload
}
export const httpCreatePatient = ({params, payload}: HttpCreatePatientOptions) =>
    axios.post(`patients`, payload, {params})

/*Get patient*/
export type HttpGetPatientOptions<TResponseType extends ResponseType> = {
    urlParams: {patientId: string}
    params: {responseType: TResponseType}
}

const HttpGetPatientResponse = z.object({
    small: PatientSmall,
    extended: PatientExtended
})

export const httpGetPatient = async <TResponseType extends ResponseType>({
    urlParams,
    params
}: HttpGetPatientOptions<TResponseType>) => {
    const response = await axios.get(`patients/${urlParams.patientId}`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetPatientResponse.shape[params.responseType]
    })
}

/*Update patient*/
export const HttpUpdatePatientPayload = z
    .object({
        lastName: z.string(),
        firstName: z.string(),
        gender: z.string(),
        birthCityId: z.string().nullable(),
        birthCountryId: z.string(),
        maritalStatus: z.string(),
        birthDate: z.string(),
        citizenshipId: z.string(),
        taxCodeType: PatientTaxCodeType,
        taxCode: z.string(),
        residenceAslDistrictId: z.string().nullable(),
        residence: z.object({
            address: z.string(),
            streetNumber: z.string(),
            city: z.string(),
            postalCode: z.string(),
            municipalityId: z.string().nullable(),
            latitude: z.string().optional(),
            longitude: z.string().optional()
        }),
        domicileAslDistrictId: z.string().nullable(),
        domicile: z
            .object({
                address: z.string(),
                streetNumber: z.string(),
                city: z.string(),
                postalCode: z.string(),
                municipalityId: z.string().nullable(),
                latitude: z.string().optional(),
                longitude: z.string().optional()
            })
            .optional(),
        contacts: z.array(Contact).min(2),
        education: PatientQualification,
        occupation: PatientOccupation,
        serviceType: PatientAgeRange,
        protectedCategory: z.boolean(),
        legalProtection: z.boolean(),
        transportationMode: PatientTransportationMode,
        localAuthorityAssistanceService: z.boolean(),
        disability: PatientDisability,
        fm01: z.boolean(),
        privacy1: z.boolean(),
        privacy2: z.boolean(),
        privacy3: z.boolean(),
        privacy4: z.boolean(),
        consentDateFm01: z.string().nullable(),
        notes: z.string().optional(),
        neuromuscularDiagnosisId: z.string().optional()
    })
    .partial()
export type HttpUpdatePatientPayload = z.infer<typeof HttpUpdatePatientPayload>

export type HttpUpdatePatientOptions = {
    urlParams: {patientId: string}
    params: {responseType: ResponseType}
    payload: HttpUpdatePatientPayload
}

export const httpUpdatePatient = ({urlParams, params, payload}: HttpUpdatePatientOptions) =>
    axios.patch(`patients/${urlParams.patientId}`, payload, {params})

/*Change patient status*/
export const HttpChangePatientPayload = z.object({
    status: PatientStatus
})
export type HttpChangePatientPayload = z.infer<typeof HttpChangePatientPayload>

export type HttpChangePatientOptions = {
    urlParams: {patientId: string}
    params: {responseType: ResponseType}
    payload: HttpChangePatientPayload
}
export const httpChangePatientStatus = ({urlParams, payload, params}: HttpChangePatientOptions) =>
    axios.patch(`patients/${urlParams.patientId}/change-status`, payload, {params})

/*Get treatment resuptions data*/
const HttpGetPatientLastInfoOptions = z.object({
    urlParams: z.object({
        patientId: z.string()
    }),
    params: z.object({
        type: z.enum(['treatment_resumption', 'cycle_resumption']).optional()
    })
})
export type HttpGetPatientLastInfoOptions = z.infer<typeof HttpGetPatientLastInfoOptions>

export const httpGetPatientLastInfo = async ({urlParams, params}: HttpGetPatientLastInfoOptions) => {
    const response = await axios.get(`patients/${urlParams.patientId}/last-info`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: PatientLastInfo
    })
}

/*Get patient visits*/
export interface HttpGetPatientEventsOptions {
    urlParams: {patientId: string}
    params: PaginationParams
    payload?: TableFiltersPayload & {
        sorts?: TableOrder[]
    }
}
export const HttpGetPatientEventsResponse = z.object({
    data: z.array(EventSmall),
    meta: PaginationResponse
})
export type HttpGetPatientEventsResponse = z.infer<typeof HttpGetPatientEventsResponse>

export const httpGetPatientEvents = async ({urlParams, params, payload}: HttpGetPatientEventsOptions) => {
    const response = await axios.post(`/patients/${urlParams.patientId}/events`, payload, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetPatientEventsResponse
    })
}

/*Export patient visits*/
interface HttpExportPatientEventsOptions {
    urlParams: {patientId: string}
    payload: {
        selectedVisitIds: string[]
        exceptedVisitIds: string[]
        type: 'xlsx' | 'xls' | 'csv'
        filters?: TableFiltersPayload['filters']
        fields?: PatientEventsColumn[]
        sorts?: TableOrder[]
    }
}
export const httpExportPatientEvents = ({urlParams, payload}: HttpExportPatientEventsOptions) =>
    axios.post(`patients/${urlParams.patientId}/events/export`, payload, {responseType: 'blob'})
